import { useCallback } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { useNavigationActions } from '@jira/polaris__component-navigation-store/src/useNavigationActions.tsx';

export type OnViewCreated = (params: { createdViewId: string; viewUrl: string }) => Promise<void>;

// keep it as a separate function to make it easier to test
export const performNavigationWithReload: (url: string) => void = (url) => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	window.location.href = url;
};

/**
 * Returns a callback that loads the freshly created view into the navigation
 * store (flagged as manually created) so the root container's synchronizer can
 * load its prefilled data and route to it - opening the new view without a full
 * page reload.
 *
 * This hook runs inside the sidebar's NavigationContainer, so `loadView` is bound
 * to a navigation store that has `navigationRemote` available.
 */
const useOnViewCreatedNew = (parentSectionId?: string): OnViewCreated => {
	const { loadView } = useNavigationActions();

	return useCallback(
		({ createdViewId, viewUrl }) =>
			new Promise<void>((resolve) => {
				loadView(createdViewId, {
					parentSectionId,
					isManuallyCreated: true,
					onFinish: (resolution) => {
						if (resolution.status === 'alreadyExisted') {
							// This may happen if a realtime event beat our mutation response.
							// Realtime updates are not added yet so we need to test them first with this feature.
							// Until this we use a hard refresh as a reliable fallback.
							// TODO: implement SPA redirect when realtime updates are added (POL-22509)
							performNavigationWithReload(viewUrl);
							return;
						}

						if (resolution.status === 'error') {
							fireErrorAnalytics({
								error: resolution.error,
								attributes: { message: 'Failed to load the view created from a template' },
								meta: {
									id: 'createViewFromTemplate',
									packageName: 'jiraViewTemplateLibrary',
									teamName: 'juno',
								},
							});

							// Just do a hard refresh if something goes wrong
							performNavigationWithReload(viewUrl);

							// Intentionally leave the promise unsettled: the hard navigation tears
							// down the page, so resolving here would let the caller close the modal
							// and briefly flash the underlying UI before the new page loads.
							return;
						}

						resolve();
					},
				});
			}),
		[loadView, parentSectionId],
	);
};

/**
 * Legacy behaviour: the modal performs a full page reload on its own, so the
 * trigger does not need to do anything when a view is created.
 */
const useOnViewCreatedOld = (_parentSectionId?: string): OnViewCreated =>
	useCallback(() => Promise.resolve(), []);

export const useOnViewCreated: (parentSectionId?: string) => OnViewCreated = functionWithCondition(
	() => fg('pol-21863_refresh_views'),
	useOnViewCreatedNew,
	useOnViewCreatedOld,
);
